import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  CanDeactivateFn,
  ResolveFn,
  Router,
  RouterStateSnapshot,
  Routes
} from '@angular/router';
import { StateManagerService } from "@wms/core";
import {inject} from "@angular/core";
import {BaseVariableService} from "@wms/variables";
import {PreferenceService, ProjectService, ServiceFactoryService, TypeFactoryService} from "@wms/services";
import { SECURITY_CHILD_ROUTES } from '@wms/settings/security';
import { ARTIFACTS_CHILD_ROUTES } from '@wms/settings/artifacts';
import { PREFAB_CONFIGURATION_CHILD_ROUTES, Config, PrefabConfigurationService } from "@wms/settings/prefab-config";
import { PROFILE_CONFIG_CHILD_ROUTES } from "@wms/settings/profile-configuration";
import { SettingsComponent } from "@wms/app/workspace/settings-workspace/settings.component";
import { ApiWorkspaceComponent } from './workspace/api-workspace/api-workspace.component';
import { JavaServiceWorkspaceComponent } from './workspace/java-service-workspace/java-service-workspace.component';
import { BUILDPREFERENCES_CHILD_ROUTES } from '@wms/settings/build-preferences';
import { RegisterWidgetProps } from "@wms/design-container";
import { StyleComponent } from './workspace/style-workspace/style.component';
import { RN_APPCONFIGURATIONS_CHILD_ROUTES } from './workspace/settings-workspace/rn-app-configurations/routes/rn-app-configurations.routes';

export const appVarResolver: ResolveFn<any> = () => {
  const typeFactoryService = inject(TypeFactoryService);
  const widgetsPropsProvider = new RegisterWidgetProps();
  widgetsPropsProvider.register();
  return Promise.all([
    inject(BaseVariableService).initAppVariables(),
    inject(ServiceFactoryService).getServicesWithType()
  ]);
};

export const prefabConfigResolver: ResolveFn<Config> = () => {
  return inject(PrefabConfigurationService).fetchPrefabConfig()
}

const canActivatePrefabConfig: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(ProjectService).isPrefabType();
};

const canActivateProfileConfig: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  return inject(ProjectService).isApplicationType();
};

const canDeActivateAPIWs: CanDeactivateFn<any> = (component: ApiWorkspaceComponent, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState) => {
  if(component.isWsDirty()){
    component.setNextState(nextState)
    component.dialogService.open('unsavedChangesDialog_api')
    return false
  }
  return true
}

const MESSAGE_STYLE_UNSAVED_CHANGES= "${0} has been modified. Do you want to save the changes?";

const canDeActivateStyle: CanDeactivateFn<any> = (component: StyleComponent, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState) => {
  if(component.activeWS.isWSDirty()) {
    component.nextState = nextState;
    component.unsavedChangesMessage = MESSAGE_STYLE_UNSAVED_CHANGES.replace("${0}",component.activeWSCategory );
    component.dialogService.open('unsavedChangesDialog_style');
    return false
  }
  return true;
};

const MESSAGE_UNSAVED_CHANGES= "This ${0} has been modified. Do you want to save the changes?";
const canDeActivateSettings: CanDeactivateFn<any> = (component: SettingsComponent, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState) => {
  if(component.activeWS.isWSDirty()) {
    component.nextState = nextState;
    component.unsavedChangesMessage = MESSAGE_UNSAVED_CHANGES.replace("${0}",component.settingHeaderService.activeWSCategory );
    component.dialogService.open('unsavedChangesDialog_settings');
    return false
  }
  return true;
};

const canDeActivateJavaServiceWs: CanDeactivateFn<any> = (component: JavaServiceWorkspaceComponent, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot, nextState) => {
  //@ts-ignore
  if (component.javaServiceHeaderService.canDeactivateWs && !component.wsManager.isDirty) {
    return true;
  }
  component.dialogService.open("unsavedChangesDialog_javaService");
  component.javaServiceHeaderService.nextState = nextState;
  return false;
};

export const APP_ROUTES: Routes = [
  {
    path: '', redirectTo: 'page', pathMatch: 'full'
  },
  {
    path: 'page',
    resolve: {
      appVariables: appVarResolver
    },
    loadComponent: () => import('./workspace/page-workspace/page-workspace.component').then((c) => c.PageWorkspaceComponent),
    children: [
      {
        path: ':pageName',
        loadComponent: () => import('./design-container/runtime/components/page-wrapper.component').then((c) => c.PageWrapperComponent),
      }
    ]
  },
  {
    path: 'database',
    loadComponent: () => import('./workspace/database-workspace/database-workspace.component').then((c) => c.DatabaseWorkspaceComponent),
  },
  {
    path: 'files',
    loadComponent: () => import('./workspace/file-workspace/file-workspace.component').then((c) => c.FileWorkspaceComponent),
    providers: [StateManagerService]
  },
  {
    path: 'apis',
    loadComponent: () => import('./workspace/api-workspace/api-workspace.component').then((c) => c.ApiWorkspaceComponent),
    canDeactivate: [canDeActivateAPIWs],
    children: [
      {
        path: 'rest/:servicename',
        loadComponent: () =>
          import(
            './workspace/api-workspace/rest-import-ui/rest-import-ui.component'
          ).then((c) => c.RestImportUiComponent),
      },
      {
        path: 'websocket/:servicename',
        loadComponent: () =>
          import(
            './workspace/api-workspace/websocket/websocket.component'
          ).then((c) => c.WebsocketComponent),
      },
      {
        path: ':apiName',
        loadComponent: () => import('./workspace/api-workspace/api-workspace.component').then((c) => c.ApiWorkspaceComponent),
      },
      {
        path: ':apiName/:endPoint',
        loadComponent: () => import('./workspace/api-workspace/swagger-ui/swagger-ui.component').then((c) => c.SwaggerUiComponent),
      },
      {
        path: ':apiName/settings',
        loadComponent: () => import('./workspace/api-workspace/api-settings/api-settings.component').then((c) => c.ApiSettingsComponent),
      },
      {
        path: 'import',
        loadComponent: () => import('./workspace/api-workspace/empty-screen/empty-screen.component').then((c) => c.EmptyScreenComponent),
      }
    ]},
  {
    path: "settings",
    loadComponent: () => import('./workspace/settings-workspace/settings.component').then((c) => c.SettingsComponent),
    canDeactivate: [canDeActivateSettings],
    children: [
      {
        path: '',
        redirectTo: 'general',
        pathMatch: 'full',
      },
      {
        path: 'general',
        data: { workspace: 'General' },
        loadComponent: () => import('./workspace/settings-workspace/general/general.component').then((c) => c.GeneralComponent)
      },
      {
        path: 'profile-configuration',
        canActivateChild: [canActivateProfileConfig],
        loadComponent: () => import('./workspace/settings-workspace/profile-configuration/profile-configuration.component').then((c) => c.ProfileConfigurationComponent),
        data: { workspace: 'Profile Configuration' },
        children: PROFILE_CONFIG_CHILD_ROUTES,
      },
      {
        path: 'prefab-configuration',
        resolve: { prefabConfig: prefabConfigResolver },
        canActivateChild: [canActivatePrefabConfig],
        data: { workspace: 'Prefab Configuration' },
        loadComponent: () => import('./workspace/settings-workspace/prefab-configuration/prefab-configuration.component').then(c => c.PrefabConfigurationComponent),
        children: PREFAB_CONFIGURATION_CHILD_ROUTES
      },
      {
        path: 'users-and-permissions',
        data: { workspace: 'Users & Permissions' },
        loadComponent: () => import('./workspace/settings-workspace/users-management/users-management.component').then((c) => c.UsersManagementComponent)
      },
      {
        path: 'security',
        data: { workspace: 'Security' },
        loadComponent: () => import('./workspace/settings-workspace/security/security.component').then((c) => c.SecurityComponent),
        children: SECURITY_CHILD_ROUTES
      },
      {
        path: 'artifacts',
        data: { workspace: 'Artifacts' },
        loadComponent: () => import('./workspace/settings-workspace/artifacts/artifacts.component').then((c) => c.ArtifactsComponent),
        children: ARTIFACTS_CHILD_ROUTES
      },
      {
        path: 'build-preferences',
        data: { workspace: 'Build Preferences' },
        loadComponent: () => import('./workspace/settings-workspace/build-preferences/build-preferences.component').then((c) => c.BuildPreferencesComponent),
        children: BUILDPREFERENCES_CHILD_ROUTES
      },
      {
        path: 'app-configurations',
        data: { workspace: 'App Configurations' },
        loadComponent: () => import('./workspace/settings-workspace/rn-app-configurations/rn-app-configurations.component').then((c) => c.RNAppConfigurationsComponent),
        children: RN_APPCONFIGURATIONS_CHILD_ROUTES
      },
      {
        path: 'themes',
        data: { workspace: 'Themes' },
        loadComponent: () => import('./workspace/settings-workspace/themes/themes.component').then((c) => c.ThemesComponent)
      },
      {
        path: 'localized-messages',
        data: { workspace: 'Localized Messages' },
        loadComponent: () => import('./workspace/settings-workspace/localized-messages/localized-messages.component').then((c) => c.LocalizedMessagesComponent)
      }
    ]
  },
  {
    path: "style",
    canDeactivate: [canDeActivateStyle],
    loadComponent: () => import('./workspace/style-workspace/style.component').then((c) => c.StyleComponent),
    children: [
      {
        path: '',
        redirectTo: 'foundation',
        pathMatch: 'full',
      },
      {
        path: 'foundation',
        data: { workspace: "Global" },
        loadComponent: () =>
          import("./workspace/style-workspace/foundation/foundation.component").then(
            (c) => c.FoundationComponent
          ),
      },
      {
        path: 'widgets',
        data: { workspace: "Components" },
        children: [
          {
            path: '',
            redirectTo: 'button',
            pathMatch: 'full',
          },
          {
            path: ':component',
            data: { workspace: "Component" },
            loadComponent: () =>
              import("./workspace/style-workspace/widgets/widgets.component").then(
                (c) => c.WidgetsComponent
              ),
          },
        ]
      }
    ]
  },
  {
    path: 'java-service',
    loadComponent: () => import('./workspace/java-service-workspace/java-service-workspace.component').then((c) => c.JavaServiceWorkspaceComponent),
    canDeactivate: [canDeActivateJavaServiceWs],
    children: [{
      path: ':service',
      loadComponent: () => import('./workspace/java-service-workspace/java-service-workspace.component').then((c) => c.JavaServiceWorkspaceComponent),
    }]
  },
  {
    path:'mergechanges',
    loadComponent: () => import('./workspace/merge-changes/merge-changes.component').then((c) => c.MergeChangesComponent),
  },
  {
    path: '**', redirectTo: '/page'
  }
];

export const PREVIEW_ROUTES: Routes = [
  {
    path: 'preview',
    loadComponent: () => import('../preview/preview.component').then((c) => c.PreviewComponent),
  }
];
